import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { gdtConstants, gdtHelpers } from '@medflex/gdt';
import SelectMenu, { Item as SelectMenuItem } from '@medflex/components/stories/SelectMenu';
import UploadFileSettingsBox from './UploadFileSettingsBox';
import PathSettingsBox from './PathSettingsBox';
import SettingsInputField from './SettingsInputField';

const { PvsSelectOptions, GdtFeatures } = gdtConstants;

const PVSIntegrationModule = () => {
  const { t } = useTranslation('physician', { keyPrefix: 'PVSIntegrationModule' });

  const [selectedPVS, setSelectedPVS] = useState(gdtHelpers.selectedPVS.get() || '');

  const handleSelectPVS = value => {
    setSelectedPVS(value);
    gdtHelpers.selectedPVS.set(value);
  };

  const setValuesSyncPatients = ({ path, fileName }) => {
    gdtHelpers.syncPatients.setPath(path);
    gdtHelpers.syncPatients.setFileName(fileName);
  };

  const setValuesOpenPatientInPVS = ({ path, fileName }) => {
    gdtHelpers.openPatientInPVS.setPath(path);
    gdtHelpers.openPatientInPVS.setFileName(fileName);
  };

  const setValuesOpenPatientInMedflex = ({ path, fileName }) => {
    gdtHelpers.openPatientInMedflex.setPath(path);
    gdtHelpers.openPatientInMedflex.setFileName(fileName);
  };

  const setValuesWriteExamination = ({ path, fileName, downloadPath }) => {
    gdtHelpers.writeExamination.setPath(path);
    gdtHelpers.writeExamination.setFileName(fileName);
    gdtHelpers.writeExamination.setDownloadPath(downloadPath);
  };

  return (
    <div data-component="PVSIntegrationModule" className="flex flex-col space-y-6">
      <header className="mb-6 flex items-center">
        <h2 className="text-2xl font-semibold">{t('title')}</h2>
      </header>
      <p className="whitespace-pre-line">
        <Trans t={t} i18nKey="description">
          <a
            href="https://medflex.de/hilfe/pvs-integration/start/"
            target="_blank"
            className="hover:underline text-blue-500"
            rel="noreferrer"
          >
            here
          </a>
        </Trans>
      </p>
      <div>
        <p className="font-semibold mb-2">{t('pvsSelect.title')}</p>
        <SelectMenu
          label={selectedPVS ? t(`pvsSelect.options.${selectedPVS}`) : t('pvsSelect.placeholder')}
        >
          <div className="overflow-auto max-h-[250px]">
            {Object.entries(PvsSelectOptions).map(([key, value]) => (
              <SelectMenuItem
                className="w-full"
                key={key}
                value={key}
                label={t(`pvsSelect.options.${value}`)}
                name="selectedPVS"
                onChange={() => handleSelectPVS(key)}
              />
            ))}
            <SelectMenuItem
              className="w-full"
              value=""
              label={t('pvsSelect.options.other')}
              name="selectedPVS"
              onChange={() => handleSelectPVS('')}
            />
          </div>
        </SelectMenu>
      </div>
      <div className="border-t border-slate-300 flex-grow" />

      <PathSettingsBox feature={GdtFeatures.SYNC_PATIENTS} setValues={setValuesSyncPatients}>
        {({ register, isEdit }) =>
          isEdit ? (
            <>
              <SettingsInputField
                feature={GdtFeatures.SYNC_PATIENTS}
                fieldKey="path"
                register={register}
                defaultValue={gdtHelpers.syncPatients.getPath()}
              />
              <SettingsInputField
                feature={GdtFeatures.SYNC_PATIENTS}
                fieldKey="fileName"
                register={register}
                defaultValue={gdtHelpers.syncPatients.getFileName()}
              />
            </>
          ) : (
            <>
              <p className="font-semibold mb-2">{t(`${GdtFeatures.SYNC_PATIENTS}.path.label`)}</p>
              <p className="mb-4">{gdtHelpers.syncPatients.getPath() || '-'}</p>
              <p className="font-semibold mb-2">
                {t(`${GdtFeatures.SYNC_PATIENTS}.fileName.label`)}
              </p>
              <p className="mb-4">{gdtHelpers.syncPatients.getFileName() || '-'}</p>
            </>
          )
        }
      </PathSettingsBox>

      {selectedPVS === PvsSelectOptions.MEDICAL_OFFICE && (
        <PathSettingsBox
          feature={GdtFeatures.OPEN_PATIENT_IN_PVS}
          setValues={setValuesOpenPatientInPVS}
        >
          {({ register, isEdit }) =>
            isEdit ? (
              <>
                <SettingsInputField
                  feature={GdtFeatures.OPEN_PATIENT_IN_PVS}
                  fieldKey="path"
                  register={register}
                  defaultValue={gdtHelpers.openPatientInPVS.getPath()}
                />
                <SettingsInputField
                  feature={GdtFeatures.OPEN_PATIENT_IN_PVS}
                  fieldKey="fileName"
                  register={register}
                  defaultValue={gdtHelpers.openPatientInPVS.getFileName()}
                />
              </>
            ) : (
              <>
                <p className="font-semibold mb-2">
                  {t(`${GdtFeatures.OPEN_PATIENT_IN_PVS}.path.label`)}
                </p>
                <p className="mb-4">{gdtHelpers.openPatientInPVS.getPath() || '-'}</p>
                <p className="font-semibold mb-2">
                  {t(`${GdtFeatures.OPEN_PATIENT_IN_PVS}.fileName.label`)}
                </p>
                <p className="mb-4">{gdtHelpers.openPatientInPVS.getFileName() || '-'}</p>
              </>
            )
          }
        </PathSettingsBox>
      )}

      <PathSettingsBox
        feature={GdtFeatures.OPEN_PATIENT_IN_MEDFLEX}
        setValues={setValuesOpenPatientInMedflex}
      >
        {({ register, isEdit }) =>
          isEdit ? (
            <>
              <SettingsInputField
                feature={GdtFeatures.OPEN_PATIENT_IN_MEDFLEX}
                fieldKey="path"
                register={register}
                defaultValue={gdtHelpers.openPatientInMedflex.getPath()}
              />
              <SettingsInputField
                feature={GdtFeatures.OPEN_PATIENT_IN_MEDFLEX}
                fieldKey="fileName"
                register={register}
                defaultValue={gdtHelpers.openPatientInMedflex.getFileName()}
              />
            </>
          ) : (
            <>
              <p className="font-semibold mb-2">
                {t(`${GdtFeatures.OPEN_PATIENT_IN_MEDFLEX}.path.label`)}
              </p>
              <p className="mb-4">{gdtHelpers.openPatientInMedflex.getPath() || '-'}</p>
              <p className="font-semibold mb-2">
                {t(`${GdtFeatures.OPEN_PATIENT_IN_MEDFLEX}.fileName.label`)}
              </p>
              <p className="mb-4">{gdtHelpers.openPatientInMedflex.getFileName() || '-'}</p>
            </>
          )
        }
      </PathSettingsBox>

      <PathSettingsBox
        feature={GdtFeatures.WRITE_EXAMINATION}
        setValues={setValuesWriteExamination}
      >
        {({ register, isEdit, errors }) =>
          isEdit ? (
            <>
              <SettingsInputField
                feature={GdtFeatures.WRITE_EXAMINATION}
                fieldKey="path"
                register={register}
                defaultValue={gdtHelpers.writeExamination.getPath()}
              />
              <SettingsInputField
                feature={GdtFeatures.WRITE_EXAMINATION}
                fieldKey="fileName"
                register={register}
                defaultValue={gdtHelpers.writeExamination.getFileName()}
              />
              <SettingsInputField
                feature={GdtFeatures.WRITE_EXAMINATION}
                fieldKey="downloadPath"
                register={register({ maxLength: 50 })}
                defaultValue={gdtHelpers.writeExamination.getDownloadPath()}
                error={errors.downloadPath}
              />
            </>
          ) : (
            <>
              <p className="font-semibold mb-2">
                {t(`${GdtFeatures.WRITE_EXAMINATION}.path.label`)}
              </p>
              <p className="mb-4">{gdtHelpers.writeExamination.getPath() || '-'}</p>
              <p className="font-semibold mb-2">
                {t(`${GdtFeatures.WRITE_EXAMINATION}.fileName.label`)}
              </p>
              <p className="mb-4">{gdtHelpers.writeExamination.getFileName() || '-'}</p>
              <p className="font-semibold mb-2">
                {t(`${GdtFeatures.WRITE_EXAMINATION}.downloadPath.label`)}
              </p>
              <p className="mb-4">{gdtHelpers.writeExamination.getDownloadPath() || '-'}</p>
            </>
          )
        }
      </PathSettingsBox>

      <UploadFileSettingsBox />
    </div>
  );
};

export default PVSIntegrationModule;
